import { useMemo, useState } from 'react';
import { AlertTriangle, Check, Trash2, X, Filter, FileText, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils/cn';
import {
  PROFORMA_REVIEW_THRESHOLD,
  type ProformaExtraction,
  type ProformaLine,
  type TariffCodeInfo,
} from '../types/trade.types';
import { TariffPicker } from './tariff-picker';

/**
 * Revisión de la proforma antes de importar.
 *
 * Esta pantalla es **obligatoria**: nada se escribe en la orden hasta que una
 * persona confirma. La clasificación arancelaria tiene consecuencias
 * tributarias y la IA se equivoca; lo que hace útil a la extracción no es que
 * acierte siempre, sino que deje el trabajo casi hecho y señale qué mirar.
 *
 * Las líneas dudosas se marcan en ámbar y se pueden filtrar para revisarlas
 * primero.
 */

export interface ProformaReviewTableProps {
  extraction: ProformaExtraction;
  onConfirm: (lines: ProformaLine[]) => Promise<void> | void;
  onCancel: () => void;
}

type EditableField = 'description' | 'quantity' | 'unitPrice' | 'unitWeightKg';

function money(value: number, currency: string): string {
  return new Intl.NumberFormat('es-BO', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(value);
}

export function ProformaReviewTable({ extraction, onConfirm, onCancel }: ProformaReviewTableProps) {
  const [lines, setLines] = useState<ProformaLine[]>(extraction.lines);
  const [onlyIssues, setOnlyIssues] = useState(extraction.needsReview > 0);
  const [saving, setSaving] = useState(false);

  const needsReview = useMemo(
    () => lines.filter((l) => l.confidence < PROFORMA_REVIEW_THRESHOLD).length,
    [lines]
  );

  const total = useMemo(() => lines.reduce((sum, l) => sum + l.quantity * l.unitPrice, 0), [lines]);

  /**
   * Líneas que van a entrar sin partida del catálogo.
   *
   * Se cuenta y se dice antes de confirmar, porque después no se nota: el ítem
   * queda con una tasa escrita y en la lista se ve igual que uno clasificado.
   * Es la diferencia entre un arancel que se actualiza con cada decreto y uno
   * congelado en el número que trajo la proforma.
   */
  const sinClasificar = useMemo(() => lines.filter((l) => !l.tariffHeadingId).length, [lines]);

  const visible = onlyIssues
    ? lines.filter((l) => l.confidence < PROFORMA_REVIEW_THRESHOLD)
    : lines;

  /**
   * Editar una línea la da por revisada: la persona ya la miró.
   * Es lo que hace que el filtro de dudosas vaya vaciándose a medida que
   * trabaja, en vez de quedarse estático.
   */
  function update(lineNumber: number, field: EditableField, raw: string) {
    setLines((prev) =>
      prev.map((line) => {
        if (line.lineNumber !== lineNumber) return line;

        const patch: Partial<ProformaLine> = { confidence: 1, issues: [] };
        if (field === 'description') patch.description = raw;
        else {
          const parsed = Number(raw);
          const value = Number.isFinite(parsed) ? parsed : 0;
          if (field === 'quantity') patch.quantity = value;
          else if (field === 'unitPrice') patch.unitPrice = value;
          else patch.unitWeightKg = raw.trim() === '' ? null : value;
        }
        return { ...line, ...patch };
      })
    );
  }

  /**
   * Clasificar una línea con una partida del catálogo.
   *
   * Guarda el **id**, no solo el código: es lo que hace que la alícuota se
   * resuelva contra el arancel vigente en cada recálculo. Se guardan también
   * `hsCode` y `gaRate` para poder leer la fila sin consultar el catálogo, pero
   * el que manda en el cálculo es el id.
   *
   * Al elegir, la línea queda revisada: la persona la miró y decidió.
   */
  function clasificar(lineNumber: number, partida: TariffCodeInfo | null) {
    setLines((prev) =>
      prev.map((line) =>
        line.lineNumber === lineNumber
          ? {
              ...line,
              tariffHeadingId: partida?.id ?? null,
              hsCode: partida?.code ?? null,
              hsDescription: partida?.description ?? null,
              gaRate: partida?.gaRate ?? null,
              confidence: 1,
              issues: [],
            }
          : line
      )
    );
  }

  function remove(lineNumber: number) {
    setLines((prev) => prev.filter((l) => l.lineNumber !== lineNumber));
  }

  async function confirm() {
    setSaving(true);
    try {
      await onConfirm(lines);
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="space-y-3">
      {/* Resumen del documento */}
      <header className="rounded-xl border bg-prixia-ink px-4 py-3 text-white">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="flex items-center gap-2 text-sm font-semibold">
              <FileText className="h-4 w-4 text-prixia-gold" />
              {extraction.fileName}
            </h3>
            <p className="mt-0.5 text-xs text-white/70">
              {extraction.supplierName ?? 'Proveedor sin identificar'}
              {extraction.proformaNumber && <> · {extraction.proformaNumber}</>}
              {extraction.incoterm && <> · {extraction.incoterm}</>}
              {' · '}
              {/*
                Sale de `usedAi` y no del formato. Un Excel normalmente se parsea
                sin IA, pero si sus columnas no se reconocen lo lee el modelo:
                deducirlo de `source === 'EXCEL'` diría "leído sin IA" sobre algo
                que sí pasó por el modelo, que es justo lo que la persona
                necesita saber para decidir cuánto revisar.
              */}
              {extraction.usedAi ? (
                <span className="inline-flex items-center gap-1">
                  <Sparkles className="h-3 w-3" />
                  {extraction.source === 'EXCEL'
                    ? 'columnas no reconocidas: lo leyó la IA'
                    : 'extraído con IA'}
                </span>
              ) : (
                'leído sin IA'
              )}
            </p>
          </div>

          <dl className="flex gap-5 text-right">
            <div>
              <dd className="text-lg font-semibold leading-none tabular-nums">{lines.length}</dd>
              <dt className="mt-1 text-[10px] uppercase tracking-wide text-white/50">líneas</dt>
            </div>
            <div>
              <dd
                className={cn(
                  'text-lg font-semibold leading-none tabular-nums',
                  needsReview > 0 ? 'text-prixia-gold' : 'text-white'
                )}
              >
                {needsReview}
              </dd>
              <dt className="mt-1 text-[10px] uppercase tracking-wide text-white/50">a revisar</dt>
            </div>
            <div>
              <dd className="text-lg font-semibold leading-none tabular-nums">
                {money(total, extraction.currency)}
              </dd>
              <dt className="mt-1 text-[10px] uppercase tracking-wide text-white/50">total</dt>
            </div>
          </dl>
        </div>
      </header>

      {extraction.warnings.length > 0 && (
        <div className="space-y-1 rounded-lg border border-alerta-borde bg-alerta-suave px-3 py-2">
          {extraction.warnings.map((w, i) => (
            <p key={i} className="flex items-start gap-1.5 text-xs leading-snug text-alerta">
              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
              {w}
            </p>
          ))}
        </div>
      )}

      {/* Barra de trabajo */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button
          size="sm"
          variant={onlyIssues ? 'default' : 'outline'}
          onClick={() => setOnlyIssues((v) => !v)}
          disabled={needsReview === 0 && !onlyIssues}
          className="gap-1.5"
        >
          <Filter className="h-3.5 w-3.5" />
          {onlyIssues ? `Viendo las ${needsReview} dudosas` : 'Ver solo las dudosas'}
        </Button>

        <p className="text-xs text-muted-foreground">
          Editá lo que haga falta. Una línea editada queda marcada como revisada.
        </p>
      </div>

      {/* Tabla */}
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full min-w-[900px] text-xs">
          <thead className="bg-muted/50">
            <tr className="border-b">
              <th className="w-8 px-2 py-2" />
              <th className="px-2 py-2 text-left font-medium">Descripción</th>
              <th className="w-20 px-2 py-2 text-right font-medium">Cant.</th>
              <th className="w-24 px-2 py-2 text-right font-medium">P. unit.</th>
              <th className="w-24 px-2 py-2 text-right font-medium">Peso kg</th>
              <th className="w-40 px-2 py-2 text-left font-medium">Partida</th>
              <th className="w-24 px-2 py-2 text-right font-medium">Subtotal</th>
              <th className="w-10 px-2 py-2" />
            </tr>
          </thead>
          <tbody>
            {visible.map((line) => {
              const dudosa = line.confidence < PROFORMA_REVIEW_THRESHOLD;
              return (
                <tr
                  key={line.lineNumber}
                  className={cn('border-b last:border-0', dudosa && 'bg-alerta-suave')}
                >
                  <td className="px-2 py-1.5 text-center">
                    {dudosa ? (
                      <AlertTriangle
                        className="mx-auto h-3.5 w-3.5 text-alerta"
                        aria-label="Necesita revisión"
                      />
                    ) : (
                      <Check className="mx-auto h-3.5 w-3.5 text-ok" aria-label="OK" />
                    )}
                  </td>

                  <td className="px-2 py-1.5">
                    <Input
                      value={line.description}
                      onChange={(e) => update(line.lineNumber, 'description', e.target.value)}
                      className="h-7 border-transparent bg-transparent px-1 text-xs hover:border-input focus:border-input"
                    />
                    {line.issues.length > 0 && (
                      <p className="mt-0.5 px-1 text-[10px] leading-tight text-alerta">
                        {line.issues.join(' · ')}
                      </p>
                    )}
                  </td>

                  <NumberCell
                    value={line.quantity}
                    onChange={(v) => update(line.lineNumber, 'quantity', v)}
                  />
                  <NumberCell
                    value={line.unitPrice}
                    onChange={(v) => update(line.lineNumber, 'unitPrice', v)}
                  />
                  <NumberCell
                    value={line.unitWeightKg}
                    onChange={(v) => update(line.lineNumber, 'unitWeightKg', v)}
                  />

                  {/*
                    Buscador del arancel, no un campo de texto.

                    🔴 Acá se tecleaba el código y la alícuota se aceptaba tal
                    como venía del documento. Es la puerta por la que entran los
                    productos de a 50, y cada línea sin clasificar es un ítem
                    que ningún decreto va a alcanzar: cuando el D.S. 5646 bajó
                    5 puntos todo el arancel, 28 de los 38 ítems cargados no se
                    enteraron porque tenían la tasa escrita a mano.

                    `rawCode` muestra lo que decía la proforma cuando no se pudo
                    resolver, para no tener que volver al documento.
                  */}
                  <td className="min-w-[260px] px-2 py-1.5">
                    <TariffPicker
                      dense
                      value={
                        line.tariffHeadingId && line.hsCode
                          ? ({
                              id: line.tariffHeadingId,
                              code: line.hsCode,
                              description: line.hsDescription ?? '',
                              gaRate: line.gaRate ?? 0,
                              ivaExempt: false,
                              hasIce: false,
                              hasIehd: false,
                            } as TariffCodeInfo)
                          : null
                      }
                      rawCode={line.tariffHeadingId ? null : (line.hsCodeRaw ?? line.hsCode)}
                      onChange={(partida) => clasificar(line.lineNumber, partida)}
                    />
                  </td>

                  <td className="px-2 py-1.5 text-right font-mono tabular-nums">
                    {(line.quantity * line.unitPrice).toFixed(2)}
                  </td>

                  <td className="px-2 py-1.5 text-center">
                    <button
                      type="button"
                      onClick={() => remove(line.lineNumber)}
                      title="Quitar esta línea"
                      className="text-muted-foreground transition-colors hover:text-bloqueo"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {visible.length === 0 && (
          <p className="py-8 text-center text-xs text-muted-foreground">
            {onlyIssues ? 'No quedan líneas dudosas. Ya podés confirmar.' : 'No hay líneas.'}
          </p>
        )}
      </div>

      {/*
        Lo que va a entrar sin clasificar, dicho antes y no después.

        No bloquea: a veces hay que importar y clasificar luego, y trabar la
        pantalla haría que alguien invente una partida con tal de avanzar. Pero
        se dice qué consecuencia tiene, que es lo que nadie sabía.
      */}
      {sinClasificar > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-alerta-borde bg-alerta-suave px-3 py-2 text-xs text-foreground">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <p className="leading-relaxed">
            <strong>
              {sinClasificar} de {lines.length}
            </strong>{' '}
            {sinClasificar === 1 ? 'línea entra' : 'líneas entran'} sin partida del arancel. El
            arancel se va a calcular con la tasa que trajo el documento y{' '}
            <strong>no se va a actualizar</strong> cuando cambie un decreto. Buscá la partida en la
            columna <em>Arancel</em> para clasificarlas.
          </p>
        </div>
      )}

      {/* Acciones */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">
          {extraction.usage && (
            <>Costo de la extracción: USD {extraction.usage.costUsd.toFixed(4)} · </>
          )}
          Nada se importa hasta que confirmes.
        </p>

        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={onCancel} disabled={saving}>
            <X className="mr-1.5 h-3.5 w-3.5" />
            Descartar
          </Button>
          <Button size="sm" onClick={() => void confirm()} disabled={saving || lines.length === 0}>
            <Check className="mr-1.5 h-3.5 w-3.5" />
            {saving ? 'Importando…' : `Confirmar ${lines.length} producto(s)`}
          </Button>
        </div>
      </div>
    </section>
  );
}

/** Celda numérica editable, alineada a la derecha. */
function NumberCell({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (raw: string) => void;
}) {
  return (
    <td className="px-2 py-1.5">
      <Input
        type="number"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className="h-7 border-transparent bg-transparent px-1 text-right font-mono text-xs tabular-nums hover:border-input focus:border-input"
      />
    </td>
  );
}
